import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { ExternalLink, FileText } from 'lucide-react';
import { getUploadUrl } from '../../lib/api';

/**
 * In-page payment-proof viewer dialog.
 * Shows JPG/PNG inline as an image and PDFs in an embedded iframe.
 * Falls back to an "Open in new tab / Download" link.
 */
export const PaymentProofDialog = ({ open, onClose, proofUrl, name }) => {
  if (!proofUrl) return null;
  // base64 data URLs are used as-is; /uploads paths are resolved via the API host.
  const resolved = proofUrl.startsWith('data:') ? proofUrl : getUploadUrl(proofUrl);
  const lower = (name || proofUrl).toLowerCase();
  const isPdf = lower.includes('.pdf') || resolved.startsWith('data:application/pdf');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="payment-proof-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-blue-600" />
            Payment Proof{name ? ` — ${name}` : ''}
          </DialogTitle>
          <DialogDescription className="sr-only">Preview of the uploaded payment proof document</DialogDescription>
        </DialogHeader>
        <div className="mt-2 bg-slate-50 rounded-lg overflow-hidden border">
          {isPdf ? (
            <iframe
              src={resolved}
              title="Payment proof"
              className="w-full"
              style={{ height: '70vh' }}
              data-testid="payment-proof-pdf"
            />
          ) : (
            <div className="flex items-center justify-center max-h-[70vh] overflow-auto p-2">
              <img
                src={resolved}
                alt="Payment proof"
                className="max-w-full max-h-[68vh] object-contain"
                data-testid="payment-proof-image"
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <a
            href={resolved}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
            data-testid="payment-proof-open-new-tab"
          >
            <ExternalLink className="w-4 h-4 mr-1" />Open in new tab / Download
          </a>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="payment-proof-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentProofDialog;
